import { Controller } from "@hotwired/stimulus"

import { createTextStream } from "../../lib/text-stream"

/**
 * AI Text Stream Controller — CONEK-3361.
 *
 * Hotwire-side adapter for the shared text-stream engine. Wraps an output
 * element and exposes `pushChunk(chunk)` + `finish()` actions for parent
 * controllers / channels to call as ActionCable chunks arrive. Visually
 * identical to the React `useTextStream` hook because both delegate to the
 * same engine (../../lib/text-stream).
 *
 * Usage:
 *   <div data-controller="ai--text-stream">
 *     <span data-ai--text-stream-target="output"></span>
 *   </div>
 *
 * Parent controller / channel:
 *   const ctrl = this.application.getControllerForElementAndIdentifier(el, "ai--text-stream")
 *   ctrl.pushChunk(text)
 *   ctrl.finish()
 *
 * The `is-streaming` class on the output target enables the blinking cursor
 * (companion.css). Reduced-motion users get an instant render with no
 * cursor blink — handled by both the engine and the CSS media query.
 */
export default class extends Controller {
  static targets = ["output"]

  connect() {
    if (!this.hasOutputTarget) {
      console.warn(
        '[ai--text-stream] missing data-ai--text-stream-target="output"; controller is inert.',
      )
      return
    }
    this.stream = createTextStream({
      target: this.outputTarget,
      onStart: () => {
        this.outputTarget.classList.add("is-streaming")
      },
      onComplete: () => {
        this.outputTarget.classList.remove("is-streaming")
      },
    })
  }

  disconnect() {
    this.stream?.cancel()
    this.stream = null
  }

  pushChunk(chunk) {
    this.stream?.pushChunk(chunk)
  }

  finish() {
    this.stream?.finish()
  }
}
